import React, { useState, useEffect, useContext, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { AuthContext } from '@/layout/Auth'
import { styled } from '@mui/material/styles'
import { secondaryText } from '@/constant'
import { Stack } from '@mui/material'
import { Row } from '../components'
import Button, { ConfirmButton } from '@/components/Button'
import { RoleInfo, PermissionInfo } from '@/types'
import Text from '@/components/Text'
import TransferList from '@/components/TransferList'
import { TreeDataProps } from '@/components/TransferList/treeNode'
import { useQuery, useMutation } from '@tanstack/react-query'
import { getPermissions, updateRolePermissions } from '@/api'
import { Message } from '@cx/ui'

export interface RoleEditorProps {
  roleInfo: RoleInfo
  onCancel: () => void
  refetch: () => void
}

const HightlightText = styled(Text)(() => ({
  fontSize: '14px',
  weight: 'bold',
  margin: '0px 4px',
}))

const dataTransformation = (data: PermissionInfo[]) => {
  const treeData: TreeDataProps[] = []
  for (const item of data) {
    const [parentTitle, childTitle] = item.name.split('-')?.map((item) => item.trim())
    const parentNode: TreeDataProps = {
      id: parentTitle,
      name: parentTitle,
      children: [],
    }
    const childNode = { id: item.id, name: childTitle }
    if (treeData.find((treeNode) => treeNode.id === parentTitle)) {
      const targetNode = treeData.find((treeNode) => treeNode.id === parentTitle)
      targetNode!.children!.push(childNode)
    } else {
      parentNode!.children!.push(childNode)
      treeData.push(parentNode)
    }
  }
  return treeData
}

const RoleEditor: React.FC<RoleEditorProps> = (props) => {
  const navigate = useNavigate()
  const { authInfo } = useContext(AuthContext)
  const orgInfo = authInfo?.orgInfo
  const { roleInfo, onCancel, refetch } = props
  const [perms, setPerms] = useState<number[]>(roleInfo?.perms?.map((perm) => perm?.id))

  const { data: res } = useQuery(
    ['getPermissions', orgInfo?.id],
    // @ts-ignore
    () => orgInfo?.id && getPermissions({ id: orgInfo?.id, query: null }),
    {
      enabled: !!orgInfo?.id,
    },
  )
  const updatePermissionsMutation = useMutation(updateRolePermissions, {
    onSuccess: () => {
      Message.success('更新权限成功')
      refetch()
      onCancel()
    },
  })

  const onChangePerms = useCallback((permIds: number[]) => {
    setPerms(permIds)
  }, [])

  const onConfirm = useCallback(() => {
    updatePermissionsMutation.mutate({
      // @ts-ignore
      id: orgInfo?.id,
      role_id: roleInfo?.id,
      perms,
    })
  }, [orgInfo?.id, perms, roleInfo?.id, updatePermissionsMutation])

  useEffect(() => {
    if (roleInfo && orgInfo) {
      setPerms(roleInfo?.perms?.map((perm) => perm?.id))
    } else {
      navigate('/console/organization/role')
    }
  }, [navigate, orgInfo, roleInfo])

  return (
    <Stack sx={{ height: '100%', gap: '24px' }}>
      <Stack sx={{ gap: '8px' }}>
        <Text weight='bold'>修改角色权限</Text>
        <Text
          sx={{
            display: 'flex',
            fontSize: '14px',
            alignItems: 'center',
            color: secondaryText,
          }}
          weight='regular'
        >
          确认将角色
          <HightlightText weight='bold'>{roleInfo?.name}</HightlightText>
          的权限从
        </Text>
      </Stack>
      <TransferList<PermissionInfo>
        id='id'
        treeView={true}
        treeData={dataTransformation(res?.data ?? [])}
        data={res?.data ?? []}
        searchKey='name'
        checked={perms}
        // @ts-ignore
        onChange={onChangePerms}
      />
      <Row sx={{ justifyContent: 'flex-end', gap: '8px' }}>
        <Button type='secondary' onClick={onCancel}>
          取消
        </Button>
        <ConfirmButton onClick={onConfirm}>确认修改</ConfirmButton>
      </Row>
    </Stack>
  )
}

export default RoleEditor
